  // src/components/OrdersTable.tsx
import React, { useEffect, useState, useCallback, useRef, memo, useMemo } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {EyeIcon, PencilSquareIcon, TrashIcon, PlusIcon, FunnelIcon, DocumentArrowDownIcon,TruckIcon, CurrencyDollarIcon, 
   ClockIcon, UserIcon,CheckCircleIcon,CubeIcon,ShoppingBagIcon,ArrowPathIcon,DocumentTextIcon,DocumentCheckIcon,
   XCircleIcon,BoltIcon,ExclamationTriangleIcon}from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import ofimaSyncService from '../../api/ofimaSyncService';
import DataTable from '../ui/DataTable';
import { ORDER_STATES, STORE_STYLES, transportadora, metodoPago } from '../../assets/data/arrays';
import Pagination from '../ui/Pagination';
import { usePermissions } from '../../hooks/usePermissions';
import LoadingSpinner from '../LoadingSpinner';
import { useURLFilters } from '../../hooks/useURLFilters';
import { generarPDFComprobante } from '../pdf-templates/CotizacionPDF';
import MetricCard from '../ui/MetricCard';
import UpdateDocumentsModal from '../modals/UpdateDocumentsModal';
import { Button, FilterPanel } from '../ui';
import type { FilterSection } from '../ui/FilterPanel';
import CancelOrderModal from '../modals/CancelOrderModal';
import B2bOfimaFacturaProgressModal, {
  type B2bFacturaLine,
} from '../modals/B2bOfimaFacturaProgressModal';
import type { CancelOrderConfirmPayload, CancelStep } from '../modals/CancelOrderModal';
import FalabellaShippingLabelModal from '../modals/FalabellaShippingLabelModal';
import MercadoLibreShippingLabelModal from '../modals/MercadoLibreShippingLabelModal';
import IncidenciaModal from '../modals/IncidenciaModal';
import { updateOrderStatus } from '../../services/orders/updateOrderStatusService';
import { facturarRemision, crearNotaCreditoRemision } from '../../services/orders/ofimaRemisionFacturaService';
import OfimaRemisionWithConsecutivos from '../OfimaRemisionWithConsecutivos';
import type { OfimaConsecutivos } from '../../types/order';
import {
  enrichOrdersWithConsultaRemision,
  isRemisionYaFacturadaMessage,
  selectB2bOrdersPendingFactura,
} from '../../utils/orderB2bFacturaAfterSync';

// Importar logos de transportadoras
import logo99minutos from '../../assets/images/99minutos.png';
import logoEnvia from '../../assets/images/envia.png';
import logoInter from '../../assets/images/inter.png';
import logoTCC from '../../assets/images/tcc.png';
import logoCoordinadora from '../../assets/images/coordinadora.png';
import logoDeprisa from '../../assets/images/deprisa.png';
import logoServientrega from '../../assets/images/servientrega.png';
import boldIcon from '../../assets/images/bold.ico';
import ExportProgressModal from '../modals/ExportProgressModal';
import { verifyBoldPaymentOperation } from '../../services/payments/verifyBoldPaymentOperation';
import { expandOrderListStoreFiltersForBold } from '../../services/payments/boldService';
import { orderNeedsPagosDetailForBoldVerify } from '../../utils/orderInvolvesBold';
import { normalizeOrderPaymentMethod } from '../../utils/orderPaymentTransactionRules';
import {
  getOrderListAllowedStoreNames,
  resolveOrderListStores,
  sanitizeStoreFilterValues,
} from '../../constants/managerMarketplaceRole';
import { userMatchesOrderSalesAgentFilter } from '../../constants/roleIds';
import { User, UsersResponse } from '../../types/users';

interface Order {
  autoIncrementId?: number;
  id: number;
  orders_id: string;
  origin_order_id?: string;
  store_name: string;
  id_remision?: string;
  ofima_consecutivos?: OfimaConsecutivos | null;
  first_name?: string;
  last_name: string;
  customer_full_name?: string;
  document: string;
  date_created: string;
  status: string;
  guia: string;
  total: string;
  transportadora?: string;
  payment_method?: string;
  transaction_id?: string;
  documento1?: string;
  documento2?: string;
  extraDetails?: {
    payment_method?: string;
    transaction_id?: string;
    documento1?: string;
    documento2?: string;
    tipo_cliente?: string;
    pagos?: Array<{ metodo_pago?: string; id_transaccion?: string; id?: number }>;
  };
  pagos?: Array<{ metodo_pago?: string; id_transaccion?: string; id?: number }>;
  /** Algunos listados envían tipo de cliente en raíz */
  tipo_cliente?: string;
}

const OrdersTable: React.FC = () => {
  const { user } = useAuth();
  const allowedStoreNames = getOrderListAllowedStoreNames(user?.role_id);
  const [orders, setOrders] = useState<Order[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('date_created');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedStates, setSelectedStates] = useState<{ label: string; value: string }[]>([]);
  const [selectedStores, setSelectedStores] = useState<{ label: string; value: string }[]>([]);
  const [selectedTransportadoras, setSelectedTransportadoras] = useState<{ label: string; value: string }[]>([]);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<{ label: string; value: string }[]>([]);
  const [selectedDestinos, setSelectedDestinos] = useState<{ label: string; value: string }[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [filterByState, setFilterByState] = useState(false);
  const [filterByStore, setFilterByStore] = useState(false);
  const [filterByDate, setFilterByDate] = useState(false);
  const [filterByTransportadora, setFilterByTransportadora] = useState(false);
  const [filterByPaymentMethod, setFilterByPaymentMethod] = useState(false);
  const [filterByDestino, setFilterByDestino] = useState(false);
  const [filterByAgent, setFilterByAgent] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [salesAgents, setSalesAgents] = useState<User[]>([]);
  const { hasPermission } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [totalPedidos, setTotalPedidos] = useState<number>(0);
  const [statistics, setStatistics] = useState<any>({});
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showOriginOrderId, setShowOriginOrderId] = useState(false);
  const [showFullDateTime, setShowFullDateTime] = useState(false);
  const isInitialLoad = useRef(true);
  const [updateDocumentsModal, setUpdateDocumentsModal] = useState<{
    isOpen: boolean;
    orderId: number;
    documento1?: string;
    documento2?: string;
    transportadora?: string;
    guia?: string;
    storeName?: string;
  }>({
    isOpen: false,
    orderId: 0,
    documento1: '',
    documento2: '',
    transportadora: '',
    guia: '',
    storeName: ''
  });

  const [cancelOrderModal, setCancelOrderModal] = useState<{
    isOpen: boolean;
    order: Order | null;
  }>({
    isOpen: false,
    order: null
  });

  const [falabellaShippingModal, setFalabellaShippingModal] = useState<{
    isOpen: boolean;
    order: Order | null;
  }>({
    isOpen: false,
    order: null
  });

  const [mercadolibreShippingModal, setMercadoLibreShippingModal] = useState<{
    isOpen: boolean;
    order: Order | null;
  }>({
    isOpen: false,
    order: null
  });

  const [incidenciaModal, setIncidenciaModal] = useState<{
    isOpen: boolean;
    order: Order | null;
    targetStatus: 'incidencia' | 'incidencia-resuelta' | null;
  }>({
    isOpen: false,
    order: null,
    targetStatus: null
  });

  const [isUpdatingIncidencia, setIsUpdatingIncidencia] = useState(false);

  const [isCancelling, setIsCancelling] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isVerifyingBold, setIsVerifyingBold] = useState(false);
  const [boldBulkProgressOpen, setBoldBulkProgressOpen] = useState(false);
  const [boldBulkMessage, setBoldBulkMessage] = useState('');
  const [boldBulkProgress, setBoldBulkProgress] = useState({
    current: 0,
    total: 1,
    percentage: 0,
    currentPage: 0,
    totalPages: 1
  });

  const [b2bFacturaModal, setB2bFacturaModal] = useState<{
    open: boolean
    lines: B2bFacturaLine[]
    currentIndex: number
    running: boolean
  } | null>(null);

  const INITIAL_CANCEL_STEPS: CancelStep[] = [
    { key: 'consultar', label: 'Consultar remisión en OFIMA', status: 'pending' },
    { key: 'factura', label: 'Crear factura (remisión)', status: 'pending' },
    { key: 'notaCredito', label: 'Crear nota crédito', status: 'pending' },
    { key: 'cancelar', label: 'Cancelar pedido en el sistema', status: 'pending' }
  ];
  const [cancelSteps, setCancelSteps] = useState<CancelStep[]>(INITIAL_CANCEL_STEPS);

  // Estado para rastrear documentos ya abiertos - usando Map para mejor rendimiento
  const [openedDocuments, setOpenedDocuments] = useState<Map<number, { doc1: boolean; doc2: boolean }>>(new Map());

  // Estado para guardar mensajes de pedidos sin remisión (orderId -> mensaje)
  const [mensajesSinRemision, setMensajesSinRemision] = useState<Map<number, string>>(new Map());

  // Clave para localStorage
  const LOCALSTORAGE_KEY = 'ofima_pedidos_pendientes_saldo';

  // Funciones helper para localStorage
  const guardarMensajesEnLocalStorage = useCallback((mensajes: Map<number, string>) => {
    try {
      // Solo guardar los que son "Pendiente Saldo" o variaciones
      const mensajesPendientesSaldo = new Map<number, string>();
      mensajes.forEach((mensaje, orderId) => {
        const mensajeLower = mensaje.toLowerCase();
        if (mensajeLower.includes('pendiente') && mensajeLower.includes('saldo')) {
          mensajesPendientesSaldo.set(orderId, mensaje);
        }
      });
      
      const objetoParaGuardar: Record<string, string> = {};
      mensajesPendientesSaldo.forEach((mensaje, orderId) => {
        objetoParaGuardar[orderId.toString()] = mensaje;
      });
      
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(objetoParaGuardar));
    } catch (error) {
      console.error('Error guardando en localStorage:', error);
    }
  }, []);

  const cargarMensajesDesdeLocalStorage = useCallback((): Map<number, string> => {
    try {
      const datosGuardados = localStorage.getItem(LOCALSTORAGE_KEY);
      if (!datosGuardados) return new Map();
      
      const objetoGuardado = JSON.parse(datosGuardados) as Record<string, string>;
      const mensajes = new Map<number, string>();
      
      Object.entries(objetoGuardado).forEach(([orderId, mensaje]) => {
        mensajes.set(Number(orderId), mensaje);
      });
      
      return mensajes;
    } catch (error) {
      console.error('Error cargando desde localStorage:', error);
      return new Map();
    }
  }, []);

  const limpiarPedidosConRemision = useCallback((orders: Order[]) => {
    // Limpiar del localStorage y del estado los pedidos que ya tienen remisión
    setMensajesSinRemision(prev => {
      const updated = new Map(prev);
      let hayCambios = false;
      
      orders.forEach(order => {
        const idRemisionStr = order.id_remision ? String(order.id_remision).trim() : '';
        if (idRemisionStr !== '' && updated.has(order.id)) {
          updated.delete(order.id);
          hayCambios = true;
        }
      });
      
      if (hayCambios) {
        guardarMensajesEnLocalStorage(updated);
      }
      
      return updated;
    });
  }, [guardarMensajesEnLocalStorage]);

  const { getFiltersFromURL, updateURL } = useURLFilters();

  // Componente memoizado para celda de documento 1
  const Document1Cell = memo(({ 
    row, 
    isOpened, 
    onOpenDocument,
    onFalabellaShippingLabel,
    onMercadoLibreShippingLabel
  }: { 
    row: Order; 
    isOpened: boolean; 
    onOpenDocument: (orderId: number, docType: 'doc1' | 'doc2') => void;
    onFalabellaShippingLabel: (order: Order) => void;
    onMercadoLibreShippingLabel: (order: Order) => void;
  }) => {
    const documento1 = row.documento1 || row.extraDetails?.documento1;
    const isFalabella = row.store_name === 'Falabella';
    const isMercadoLibre = row.store_name === 'Mercado Libre' || row.store_name === 'Mercado libre';
    
    const handleClick = useCallback(() => {
      if (isFalabella) {
        // Para Falabella, abrir modal de etiqueta de envío
        onFalabellaShippingLabel(row);
      } else if (isMercadoLibre) {
        // Para MercadoLibre, abrir modal de etiqueta de envío
        onMercadoLibreShippingLabel(row);
      } else if (documento1) {
        // Para otras tiendas, abrir URL del documento
        onOpenDocument(row.id, 'doc1');
        window.open(documento1, '_blank');
      }
    }, [documento1, row, isFalabella, isMercadoLibre, onOpenDocument, onFalabellaShippingLabel, onMercadoLibreShippingLabel]);
    
    // Para Falabella y MercadoLibre, siempre mostrar como disponible
    const isAvailable = isFalabella || isMercadoLibre || documento1;
    
    return (
      <div className="flex justify-center">
        <button
          onClick={handleClick}
          disabled={!isAvailable}
          className={`p-1 rounded-lg transition-colors duration-200 select-none ${
            isAvailable 
              ? isOpened
                ? 'text-green-600 hover:text-green-800 hover:bg-green-50 cursor-pointer'
                : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50 cursor-pointer'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title={
            isFalabella
              ? 'Generar Etiqueta de Envío Falabella'
              : isMercadoLibre
                ? 'Generar Etiqueta de Envío MercadoLibre'
                : documento1 
                  ? isOpened 
                    ? 'Documento 1 abierto ✓' 
                    : 'Abrir documento 1'
                  : 'Sin documento 1'
          }
        >
          {isOpened ? (
            <DocumentCheckIcon className="h-5 w-5" />
          ) : (
            <DocumentTextIcon className="h-5 w-5" />
          )}
        </button>
      </div>
    );
  });

  // Componente memoizado para celda de documento 2
  const Document2Cell = memo(({ 
    row, 
    isOpened, 
    onOpenDocument 
  }: { 
    row: Order; 
    isOpened: boolean; 
    onOpenDocument: (orderId: number, docType: 'doc1' | 'doc2') => void;
  }) => {
    const documento2 = row.documento2 || row.extraDetails?.documento2;
    
    const handleClick = useCallback(() => {
      if (documento2) {
        onOpenDocument(row.id, 'doc2');
        window.open(documento2, '_blank');
      }
    }, [documento2, row.id, onOpenDocument]);
    
    return (
      <div className="flex justify-center">
        <button
          onClick={handleClick}
          disabled={!documento2}
          className={`p-1 rounded-lg transition-colors duration-200 select-none ${
            documento2 
              ? isOpened
                ? 'text-green-600 hover:text-green-800 hover:bg-green-50 cursor-pointer'
                : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50 cursor-pointer'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title={
            documento2 
              ? isOpened 
                ? 'Documento 2 abierto ✓' 
                : 'Abrir documento 2'
              : 'Sin documento 2'
          }
        >
          {isOpened ? (
            <DocumentCheckIcon className="h-5 w-5" />
          ) : (
            <DocumentTextIcon className="h-5 w-5" />
          )}
        </button>
      </div>
    );
  });

  // Función optimizada para manejar apertura de documentos
  const handleOpenDocument = useCallback((orderId: number, docType: 'doc1' | 'doc2') => {
    setOpenedDocuments(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(orderId) || { doc1: false, doc2: false };
      newMap.set(orderId, { ...current, [docType]: true });
      return newMap;
    });
  }, []);

  // Debounce para el search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms de delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const hasSignificantURLParams = (urlFilters: any) => {
    const hasArrayParams = [
      urlFilters.states,
      urlFilters.stores, 
      urlFilters.transportadoras,
      urlFilters.paymentMethods,
      urlFilters.destinos
    ].some(arr => arr.length > 0);
    
    const hasStringParams = [
      urlFilters.search,
      urlFilters.startDate,
      urlFilters.endDate,
      urlFilters.agente
    ].some(str => str !== '');
    
    const hasNonDefaultParams = 
      urlFilters.page !== 1 ||
      urlFilters.limit !== 30 ||
      urlFilters.sortBy !== 'date_created' ||
      urlFilters.order !== 'desc';
    
    return hasArrayParams || hasStringParams || hasNonDefaultParams;
  };

  // Cargar mensajes pendientes de saldo desde localStorage al inicializar
  useEffect(() => {
    const mensajesGuardados = cargarMensajesDesdeLocalStorage();
    if (mensajesGuardados.size > 0) {
      setMensajesSinRemision(mensajesGuardados);
    }
  }, [cargarMensajesDesdeLocalStorage]);

  // Leer filtros de la URL solo en la carga inicial
  useEffect(() => {
    if (!isInitialLoad.current) return;
    
    const urlFilters = getFiltersFromURL();
    const hasURLParams = hasSignificantURLParams(urlFilters);    
    if (urlFilters.search !== searchTerm) setSearchTerm(urlFilters.search);
    if (urlFilters.page !== currentPage) setCurrentPage(urlFilters.page);
    if (urlFilters.limit !== pageSize) setPageSize(urlFilters.limit);
    if (urlFilters.sortBy !== sortBy) setSortBy(urlFilters.sortBy);
    if (urlFilters.order !== order) setOrder(urlFilters.order);
    if (urlFilters.states.length > 0) {
      setFilterByState(true);
      setSelectedStates(urlFilters.states.map(s => ({ label: s, value: s })));
    }
    
    const urlStores = sanitizeStoreFilterValues(user?.role_id, urlFilters.stores);
    if (urlStores.length > 0) {
      setFilterByStore(true);
      setSelectedStores(urlStores.map(s => ({ label: s, value: s })));
    }
    
    if (urlFilters.transportadoras.length > 0) {
      setFilterByTransportadora(true);
      setSelectedTransportadoras(urlFilters.transportadoras.map(t => ({ label: t, value: t })));
    }
    
    if (urlFilters.paymentMethods.length > 0) {
      setFilterByPaymentMethod(true);
      setSelectedPaymentMethods(urlFilters.paymentMethods.map(p => ({ label: p, value: p })));
    }
    
    if (urlFilters.destinos.length > 0) {
      setFilterByDestino(true);
      setSelectedDestinos(urlFilters.destinos.map(d => ({ label: d, value: d })));
    }
    
    if (urlFilters.startDate || urlFilters.endDate) {
      setFilterByDate(true);
      setDateRange({ start: urlFilters.startDate, end: urlFilters.endDate });
    }

    if (urlFilters.agente) {
      setFilterByAgent(true);
      setSelectedAgent(urlFilters.agente);
    }
    
    // Solo ejecutar fetchOrders si hay parámetros en la URL o si es la primera carga
    if (hasURLParams || !hasInitialized) {
      setHasInitialized(true);
    }
    
    isInitialLoad.current = false;
  }, [getFiltersFromURL, hasInitialized, currentPage, pageSize, sortBy, order]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escribir filtros en la URL cuando cambien
  useEffect(() => {
    // Solo actualizar URL si ya se inicializó para evitar bucles
    if (hasInitialized) {
      updateURL({
        search: debouncedSearchTerm,
        page: currentPage,
        limit: pageSize,
        sortBy,
        order,
        states: selectedStates.map(s => s.value),
        stores: selectedStores.map(s => s.value),
        transportadoras: selectedTransportadoras.map(t => t.value),
        paymentMethods: selectedPaymentMethods.map(p => p.value),
        destinos: selectedDestinos.map(d => d.value),
        startDate: dateRange.start,
        endDate: dateRange.end,
        agente: filterByAgent && selectedAgent ? selectedAgent : '',
      });
    }
  }, [ 
    hasInitialized,
    debouncedSearchTerm, 
    currentPage, 
    pageSize, 
    sortBy, 
    order,
    selectedStates, 
    selectedStores, 
    selectedTransportadoras,
    selectedPaymentMethods, 
    selectedDestinos, 
    dateRange,
    filterByAgent,
    selectedAgent,
    updateURL
  ]);

  const fetchOrders = useCallback(async (
    page: number, 
    limit: number, 
    search = '', 
    sortBy = 'date_created', 
    order = 'desc', 
    states: string[] = [], 
    stores: string[] = [], 
    dateRange?: { start: string; end: string },
    transportadoras: string[] = [],
    paymentMethods: string[] = [],
    destinos: string[] = [],
    agente: string = ''
  ) => {  
    try {
      setIsLoading(true);
      const token = localStorage.getItem('accessToken');
      // Solo incluir en la query si hay valores seleccionados (los arrays ya vienen filtrados)
      const statesQuery = states.length > 0 ? `&states=${states.join(',')}` : '';
      const effectiveStores = resolveOrderListStores(user?.role_id, stores);
      const storesQuery = effectiveStores.length > 0 ? `&stores=${effectiveStores.join(',')}` : '';
      const transportadorasQuery = transportadoras.length > 0 ? `&transportadora=${transportadoras.join(',')}` : '';
      const paymentMethodsQuery = paymentMethods.length > 0 ? `&paymentMethod=${paymentMethods.join(',')}` : '';
      const destinosQuery = destinos.length > 0 ? `&destino_pedido=${destinos.join(',')}` : '';
      const dateQuery = dateRange?.start && dateRange?.end ? `&startDate=${dateRange.start}&endDate=${dateRange.end}` : '';
      const agenteQuery = agente ? `&agente=${agente}` : '';
      const url = `/list?page=${page}&limit=${limit}&search=${search}&sortBy=${sortBy}&order=${order}${statesQuery}${storesQuery}${transportadorasQuery}${paymentMethodsQuery}${destinosQuery}${dateQuery}${agenteQuery}`;
      
      const response = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { data, meta } = response.data;
      const formattedOrders = data.map((order: Order, index: number) => ({
        ...order,
        autoIncrementId: index + 1 + (page - 1) * limit,
        customer_full_name: `${order.first_name ?? ''} ${order.last_name ?? ''}`,
        date_created: new Date(order.date_created).toLocaleString(),
      }));
      setTotalPedidos(meta.totalItems);
      setStatistics(meta.statistics);
      setOrders(formattedOrders);
      setCurrentPage(meta.currentPage);
      setTotalPages(meta.totalPages);

      // Limpiar del localStorage los pedidos que ya tienen remisión
      limpiarPedidosConRemision(formattedOrders);

      // Agregar un retraso adicional de 1 segundo después de recibir la respuesta
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [limpiarPedidosConRemision, user?.role_id]);

  const loadSalesAgents = useCallback(async () => {
    try {
      const response = await axiosInstance.get<UsersResponse>('/users/list?limit=1000');
      if (response.data.success) {
        const filtered = (response.data.data || [])
          .filter(userMatchesOrderSalesAgentFilter)
          .sort((a, b) =>
            `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'es')
          );
        setSalesAgents(filtered);
      } else {
        setSalesAgents([]);
      }
    } catch (error) {
      console.error('Error cargando agentes de ventas:', error);
      setSalesAgents([]);
    }
  }, []);

  useEffect(() => {
    loadSalesAgents();
  }, [loadSalesAgents]);

  const getActiveOrderListFilters = useCallback(() => {
    const states = filterByState && selectedStates.length > 0
      ? selectedStates.map(state => state.value)
      : [];
    const stores = filterByStore && selectedStores.length > 0
      ? selectedStores.map(store => store.value)
      : [];
    const transportadoras = filterByTransportadora && selectedTransportadoras.length > 0
      ? selectedTransportadoras.map(t => t.value)
      : [];
    const paymentMethods = filterByPaymentMethod && selectedPaymentMethods.length > 0
      ? selectedPaymentMethods.map(p => p.value)
      : [];
    const destinos = filterByDestino && selectedDestinos.length > 0
      ? selectedDestinos.map(d => d.value)
      : [];
    const dateRangeFilter = filterByDate && dateRange.start && dateRange.end
      ? dateRange
      : undefined;
    const agente = filterByAgent && selectedAgent ? selectedAgent : '';
    return { states, stores, transportadoras, paymentMethods, destinos, dateRangeFilter, agente };
  }, [
    filterByState,
    selectedStates,
    filterByStore,
    selectedStores,
    filterByTransportadora,
    selectedTransportadoras,
    filterByPaymentMethod,
    selectedPaymentMethods,
    filterByDestino,
    selectedDestinos,
    filterByDate,
    dateRange,
    filterByAgent,
    selectedAgent,
  ]);

  const refreshOrderList = useCallback((options?: { page?: number; stores?: string[] }) => {
    const filters = getActiveOrderListFilters();
    fetchOrders(
      options?.page ?? currentPage,
      pageSize,
      debouncedSearchTerm,
      sortBy,
      order,
      filters.states,
      options?.stores ?? filters.stores,
      filters.dateRangeFilter,
      filters.transportadoras,
      filters.paymentMethods,
      filters.destinos,
      filters.agente
    );
  }, [
    getActiveOrderListFilters,
    fetchOrders,
    currentPage,
    pageSize,
    debouncedSearchTerm,
    sortBy,
    order,
  ]);

  const handleUpdateDocuments = (order: Order) => {
    setUpdateDocumentsModal({
      isOpen: true,
      orderId: order.id,
      documento1: order.documento1 || order.extraDetails?.documento1 || '',
      documento2: order.documento2 || order.extraDetails?.documento2 || '',
      transportadora: order.transportadora || '',
      guia: order.guia || '',
      storeName: order.store_name
    });
  };

  const handleDocumentsUpdateSuccess = () => {
    // Refrescar la lista de pedidos después de actualizar
    if (hasInitialized) {
      refreshOrderList();
    }
  };

  const handleCancelOrder = (order: Order) => {
    setCancelSteps(INITIAL_CANCEL_STEPS);
    setCancelOrderModal({
      isOpen: true,
      order: order
    });
  };

  const handleFalabellaShippingLabel = (order: Order) => {
    setFalabellaShippingModal({
      isOpen: true,
      order: order
    });
  };

  const handleMercadoLibreShippingLabel = (order: Order) => {
    setMercadoLibreShippingModal({
      isOpen: true,
      order: order
    });
  };

  const handleConfirmCancelOrder = async (confirmPayload: CancelOrderConfirmPayload) => {
    const orderToCancel = cancelOrderModal.order;
    if (!orderToCancel) return;

    const { ncParcial, ncPartialLineas } = confirmPayload

    let idRemisionStr = orderToCancel.id_remision ? String(orderToCancel.id_remision).trim() : '';
    const tipodcto = 'RE';
    const ofimaBody = {
      sglOrderId: orderToCancel.orders_id,
      numeroPedido: orderToCancel.orders_id,
      usuario: localStorage.getItem('userEmail') || 'AUTO.API.POS'
    };

    // Ajustar etiqueta del paso 2 según el modo de NC
    setCancelSteps(prev =>
      prev.map(s => (s.key === 'notaCredito' ? { ...s, label: ncParcial ? 'Crear nota crédito (parcial)' : 'Crear nota crédito' } : s))
    )

    const setStep = (index: number, status: CancelStep['status'], message?: string) => {
      setCancelSteps(prev => prev.map((s, i) =>
        i === index ? { ...s, status, message } : s
      ));
    };

    const doCancelInSgl = async (
      ofimaConsecutivos?: { FS?: string; NC?: string }
    ) => {
      const token = localStorage.getItem('accessToken');
      const updateData: Record<string, unknown> = {
        status: 'cancelled',
        meta_data: [
          { key: 'fecha_cancelacion', value: new Date().toISOString() },
          { key: 'cancelado_por', value: localStorage.getItem('userEmail') || 'Sistema' }
        ]
      };
      if (ofimaConsecutivos?.FS) {
        updateData.FS = ofimaConsecutivos.FS;
      }
      if (ofimaConsecutivos?.NC) {
        updateData.NC = ofimaConsecutivos.NC;
      }
      await axiosInstance.patch(`/list/${orderToCancel.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
    };

    try {
      setIsCancelling(true);

      // Paso 0: Si no hay remisión en SGL, consultar OFIMA
      if (!idRemisionStr) {
        setStep(0, 'in_progress');
        try {
          const response = await ofimaSyncService.ejecutarYConsultar([orderToCancel.id]);
          const pedido = response?.pedidos?.find(
            (p: { Numero: string; NumeroOfima?: string | number }) =>
              String(p.Numero) === String(orderToCancel.id)
          );
          const numeroOfimaStr = pedido?.NumeroOfima ? String(pedido.NumeroOfima).trim() : '';
          if (numeroOfimaStr) {
            idRemisionStr = numeroOfimaStr;
            setStep(0, 'done');
          } else {
            setStep(0, 'done');
            setStep(1, 'skipped');
            setStep(2, 'skipped');
            setStep(3, 'in_progress');
            await doCancelInSgl();
            setStep(3, 'done');
            toast.success('Orden cancelada en el sistema (sin remisión en OFIMA).', { icon: '✅', position: 'top-center' });
            setCancelOrderModal({ isOpen: false, order: null });
            setCancelSteps(INITIAL_CANCEL_STEPS);
            if (hasInitialized) {
              refreshOrderList();
            }
            return;
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Error al consultar OFIMA';
          setStep(0, 'error', msg);
          toast.error(msg, { icon: '⚠️', position: 'top-center' });
          return;
        }
      } else {
        setStep(0, 'done');
      }

      const nrodcto = idRemisionStr;

      let consecutivoFS = '';
      let consecutivoNC = '';

      // Paso 1: Facturar remisión
      setStep(1, 'in_progress');
      try {
        const facturaRes = await facturarRemision(tipodcto, nrodcto, { ...ofimaBody, tipoFactura: 'FS' });
        consecutivoFS = String(facturaRes.data.nrodctoFactura ?? '').trim();
        setStep(1, 'done', consecutivoFS ? `FS: ${consecutivoFS}` : undefined);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error al facturar remisión';
        const normalizedMsg = msg.toLowerCase();
        const yaTieneFactura =
          normalizedMsg.includes('ya tiene una factura asociada') ||
          normalizedMsg.includes('ya tiene factura asociada') ||
          normalizedMsg.includes('remision ya tiene factura') ||
          normalizedMsg.includes('la remisión ya tiene una factura');

        if (yaTieneFactura) {
          // Si OFIMA indica que la remisión ya está facturada, continuamos con NC.
          setStep(1, 'done', 'Ya tiene una factura asociada');
          toast('La remisión ya tiene factura, se continuará con nota crédito.', {
            icon: 'ℹ️',
            position: 'top-center'
          });
        } else {
          setStep(1, 'error', msg);
          toast.error(msg, { icon: '⚠️', position: 'top-center' });
          return;
        }
      }

      // Paso 2: Crear nota crédito
      setStep(2, 'in_progress');
      try {
        const ncBody = ncParcial
          ? { ...ofimaBody, lineas: ncPartialLineas || [] }
          : ofimaBody

        const ncRes = await crearNotaCreditoRemision(tipodcto, nrodcto, ncBody);
        consecutivoNC = String(ncRes.data.nrodctoNC ?? '').trim();
        setStep(2, 'done', consecutivoNC ? `NC: ${consecutivoNC}` : undefined);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error al crear nota crédito';
        setStep(2, 'error', msg);
        toast.error(msg, { icon: '⚠️', position: 'top-center' });
        return;
      }

      // Paso 3: Cancelar pedido en el sistema (incluye consecutivos FS / NC si existen)
      setStep(3, 'in_progress');
      await doCancelInSgl({
        ...(consecutivoFS ? { FS: consecutivoFS } : {}),
        ...(consecutivoNC ? { NC: consecutivoNC } : {})
      });
      setStep(3, 'done');
      toast.success('Orden cancelada exitosamente', { icon: '✅', position: 'top-center' });
      setCancelOrderModal({ isOpen: false, order: null });
      setCancelSteps(INITIAL_CANCEL_STEPS);

      if (hasInitialized) {
        refreshOrderList();
      }
    } catch (error) {
      console.error('Error al cancelar la orden:', error);
      toast.error('Error al cancelar la orden', { icon: '⚠️', position: 'top-center' });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleIncidenciaOrder = (order: Order) => {
    // Determinar el estado objetivo según el estado actual
    let targetStatus: 'incidencia' | 'incidencia-resuelta'
    
    if (order.status === 'incidencia') {
      targetStatus = 'incidencia-resuelta'
    } else if (order.status === 'incidencia-resuelta') {
      targetStatus = 'incidencia'
    } else {
      // Si el pedido no está en ninguno de los dos estados, permitir marcarlo como incidencia
      targetStatus = 'incidencia'
    }

    setIncidenciaModal({
      isOpen: true,
      order: order,
      targetStatus: targetStatus
    })
  }

  const handleConfirmIncidencia = async (texto: string) => {
    if (!incidenciaModal.order || !incidenciaModal.targetStatus) return

    try {
      setIsUpdatingIncidencia(true)

      const updateData: any = {
        status: incidenciaModal.targetStatus,
      }

      // Agregar motivo o solución según corresponda
      if (incidenciaModal.targetStatus === 'incidencia') {
        updateData.motivo = texto
      } else {
        updateData.solucion = texto
      }

      // Hacer el primer PATCH para cambiar el estado
      await updateOrderStatus(incidenciaModal.order.id, updateData)

      // Si el cambio es a "incidencia-resuelta" y es exitoso, cambiar inmediatamente a "Asignado"
      if (incidenciaModal.targetStatus === 'incidencia-resuelta') {
        try {
          await updateOrderStatus(incidenciaModal.order.id, {
            status: 'asignado',
          })
          toast.success('Incidencia resuelta y pedido asignado exitosamente', {
            icon: '✅',
            position: 'top-center',
          })
        } catch (error: any) {
          // Si falla el cambio a "Asignado", mostrar advertencia pero el pedido ya está en "incidencia-resuelta"
          console.error('Error cambiando a Asignado:', error)
          toast.success('Incidencia resuelta exitosamente', {
            icon: '✅',
            position: 'top-center',
          })
          toast.error('No se pudo cambiar a Asignado automáticamente', {
            icon: '⚠️',
            position: 'top-center',
          })
        }
      } else {
        // Cambio a "incidencia"
        toast.success('Pedido marcado como incidencia exitosamente', {
          icon: '✅',
          position: 'top-center',
        })
      }

      // Cerrar modal
      setIncidenciaModal({ isOpen: false, order: null, targetStatus: null })

      // Refrescar la lista de pedidos
      if (hasInitialized) {
        refreshOrderList();
      }
    } catch (error: any) {
      console.error('Error al actualizar incidencia:', error)
      toast.error(
        error.message || 'Error al actualizar la incidencia',
        { icon: '⚠️', position: 'top-center' }
      )
    } finally {
      setIsUpdatingIncidencia(false)
    }
  }

  const handleForceSync = useCallback(async () => {
    try {
      setIsSyncing(true);
      
      // Filtrar pedidos que NO estén en estados excluidos del sync OFIMA
      const estadosExcluidos = [
        'eliminado',
        'verify_transaction',
        'cancelled',
        'pending',
        'fallido',
        'payment_failed',
        'devolucion_dinero',
      ];
      const pedidosFiltrados = orders.filter(order => 
        !estadosExcluidos.includes(order.status)
      );
      
      if (pedidosFiltrados.length === 0) {
        toast.error('No hay pedidos válidos para consultar', { 
          icon: 'ℹ️',
          duration: 3000,
          position: 'top-center'
        });
        return;
      }
      
      // Obtener los IDs de los pedidos filtrados
      const idsPedidos = pedidosFiltrados.map(order => order.id);
      
      // Primera consulta a OFIMA
      const firstResponse = await ofimaSyncService.ejecutarYConsultar(idsPedidos);
      let response = firstResponse;

      // Si OFIMA responde "no encontrado", enviar esos pedidos y volver a consultar
      const idsElegibles = new Set(pedidosFiltrados.map(order => String(order.id)));
      const pedidosNoEncontrados = (response?.pedidos || [])
        .filter((pedido: { Numero: string; NumeroOfima?: string; Mensaje?: string }) => {
          const numeroOfimaStr = pedido.NumeroOfima ? String(pedido.NumeroOfima).trim() : '';
          const mensaje = (pedido.Mensaje || '').toLowerCase().trim();
          const esElegible = idsElegibles.has(String(pedido.Numero));
          return esElegible && !numeroOfimaStr && mensaje === 'no encontrado';
        })
        .map((pedido: { Numero: string }) => pedido.Numero);

      if (pedidosNoEncontrados.length > 0) {
        await ofimaSyncService.sendToOfima(pedidosNoEncontrados);
        const retryResponse = await ofimaSyncService.ejecutarYConsultar(pedidosNoEncontrados);

        type PedidoOfimaConsulta = {
          Numero: string
          NumeroOfima?: string
          Procesado?: boolean | number | string
          Mensaje?: string
        }
        const pedidosMap = new Map<string, PedidoOfimaConsulta>();
        (firstResponse?.pedidos || []).forEach((pedido: PedidoOfimaConsulta) => {
          pedidosMap.set(String(pedido.Numero), pedido);
        });
        (retryResponse?.pedidos || []).forEach((pedido: PedidoOfimaConsulta) => {
          pedidosMap.set(String(pedido.Numero), pedido);
        });

        response = {
          ...firstResponse,
          ...retryResponse,
          pedidos: Array.from(pedidosMap.values())
        };
      }
      
      // Procesar respuesta y guardar mensajes de pedidos sin remisión
      setMensajesSinRemision(prev => {
        const updated = new Map(prev);
        
        if (response?.pedidos && Array.isArray(response.pedidos)) {
          response.pedidos.forEach((pedido: { Numero: string; NumeroOfima?: string | number; Mensaje?: string }) => {
            const orderId = Number(pedido.Numero);
            // Convertir NumeroOfima a string y verificar que no esté vacío
            const numeroOfimaStr = pedido.NumeroOfima ? String(pedido.NumeroOfima).trim() : '';
            const tieneRemision = numeroOfimaStr !== '';
            
            if (tieneRemision) {
              // Si tiene remisión, eliminar el mensaje si existía
              updated.delete(orderId);
            } else if (pedido.Mensaje) {
              // Si no tiene remisión y tiene mensaje, guardarlo
              updated.set(orderId, pedido.Mensaje);
            }
          });
        }
        
        // Guardar en localStorage solo los pendientes de saldo
        guardarMensajesEnLocalStorage(updated);
        
        return updated;
      });
      
      // Mostrar mensaje de éxito con información de la respuesta
      const totales = response?.totales || { total: 0, procesados: 0, noProcesados: 0 };
      const message = response?.message || 
        `Consulta completada: ${totales.procesados} procesados de ${totales.total} pedidos`;
      
      toast.success(message, { 
        icon: '✅',
        duration: 5000,
        position: 'top-center'
      });
      
      const doRefreshList = () => {
        if (hasInitialized) {
          refreshOrderList();
        }
      };

      const enriched = enrichOrdersWithConsultaRemision(pedidosFiltrados, response?.pedidos || []);
      const candidates = selectB2bOrdersPendingFactura(enriched);

      if (candidates.length > 0) {
        const usuario = localStorage.getItem('userEmail') || 'AUTO.API.POS';
        const initialLines: B2bFacturaLine[] = candidates.map((o) => ({
          orderId: o.id,
          orders_id: o.orders_id,
          remision: String(o.id_remision).trim(),
          status: 'pending'
        }));
        setB2bFacturaModal({
          open: true,
          lines: initialLines,
          currentIndex: 0,
          running: true
        });
        await new Promise((r) => setTimeout(r, 0));

        const patchLine = (lines: B2bFacturaLine[], idx: number, patch: Partial<B2bFacturaLine>) =>
          lines.map((l, i) => (i === idx ? { ...l, ...patch } : l));

        for (let i = 0; i < candidates.length; i++) {
          const o = candidates[i];
          const nrodcto = String(o.id_remision).trim();
          setB2bFacturaModal((prev) =>
            prev
              ? {
                  ...prev,
                  currentIndex: i,
                  lines: patchLine(prev.lines, i, { status: 'running' })
                }
              : prev
          );
          const ofimaBody = {
            sglOrderId: o.orders_id,
            numeroPedido: o.orders_id,
            usuario
          };
          try {
            const facturaRes = await facturarRemision('RE', nrodcto, {
              ...ofimaBody,
              idMarket: o.store_name || '',
              tipoFactura: 'FS' as const
            });
            const fsLabel = facturaRes.data?.nrodctoFactura
              ? `FS: ${facturaRes.data.nrodctoFactura}`
              : undefined;
            setB2bFacturaModal((prev) =>
              prev
                ? {
                    ...prev,
                    lines: patchLine(prev.lines, i, {
                      status: 'ok',
                      detail: fsLabel
                    })
                  }
                : prev
            );
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Error al facturar remisión';
            if (isRemisionYaFacturadaMessage(msg)) {
              setB2bFacturaModal((prev) =>
                prev
                  ? {
                      ...prev,
                      lines: patchLine(prev.lines, i, {
                        status: 'skipped',
                        detail: msg
                      })
                    }
                  : prev
              );
            } else {
              setB2bFacturaModal((prev) =>
                prev
                  ? {
                      ...prev,
                      lines: patchLine(prev.lines, i, {
                        status: 'error',
                        detail: msg
                      })
                    }
                  : prev
              );
            }
          }
        }

        setB2bFacturaModal((prev) => (prev ? { ...prev, running: false } : prev));
        await new Promise((r) => setTimeout(r, 800));
        doRefreshList();
        setB2bFacturaModal(null);
        toast.success(
          `Facturación B2B: ${candidates.length} pedido(s) procesados. Lista actualizada.`,
          { icon: '✅', duration: 4500, position: 'top-center' }
        );
      } else {
        doRefreshList();
      }
    } catch (error: any) {
      console.error('Error en consulta de remisiones OFIMA:', error);
      const errorMessage = error.message || 'Error al consultar remisiones con OFIMA';
      toast.error(errorMessage, { 
        icon: '⚠️',
        duration: 5000,
        position: 'top-center'
      });
    } finally {
      setIsSyncing(false);
    }
  
  }, [orders, hasInitialized, refreshOrderList, guardarMensajesEnLocalStorage]);

  const handleVerifyBoldOrders = useCallback(async () => {
    const bumpProgress = (cur: number, tot: number, msg: string) => {
      const t = Math.max(1, tot)
      setBoldBulkProgress({
        current: cur,
        total: t,
        percentage: Math.min(100, Math.round((cur / t) * 100)),
        currentPage: cur,
        totalPages: t
      })
      setBoldBulkMessage(msg)
    }

    try {
      setIsVerifyingBold(true)

      const token = localStorage.getItem('accessToken')
      if (!token) {
        toast.error('Error de autenticación. Inicia sesión nuevamente.', {
          icon: '⚠️',
          position: 'top-center'
        })
        return
      }

      const statusOk = (o: Order) => o.status === 'verify_transaction' || o.status === 'pending'

      const candidates = orders.filter((o) => {
        if (!statusOk(o)) return false
        const main = normalizeOrderPaymentMethod(o.payment_method || o.extraDetails?.payment_method)
        return main === 'bold' || main === 'part' || main === 'plan_separe'
      })

      if (candidates.length === 0) {
        toast.error(
          'No hay pedidos en Verificar Transacción o Pendiente con Bold, part o plan separe en la lista actual.',
          { icon: 'ℹ️', position: 'top-center' }
        )
        return
      }

      const detailNeeded = candidates.filter((o) => orderNeedsPagosDetailForBoldVerify(o))

      const listBoldMultiTx = (o: Order) => {
        const main = normalizeOrderPaymentMethod(o.payment_method || o.extraDetails?.payment_method)
        if (main !== 'bold') return false
        const raw = `${o.extraDetails?.transaction_id || o.transaction_id || ''}`
        return raw.split(',').map((s) => s.trim()).filter(Boolean).length > 1
      }

      type BoldJob = { orderId: number; storeName: string; paymentId: string; orderTotal: string }
      const jobs: BoldJob[] = []
      const seen = new Set<string>()

      const addJob = (orderId: number, storeName: string, paymentId: string, orderTotal: string) => {
        const pid = paymentId.trim()
        if (!pid) return
        const key = `${orderId}:${pid}`
        if (seen.has(key)) return
        seen.add(key)
        jobs.push({ orderId, storeName, paymentId: pid, orderTotal })
      }

      const txIdsFromField = (raw: string) => {
        const s = raw.trim()
        if (!s) return []
        if (s.includes(',')) return s.split(',').map((x) => x.trim()).filter(Boolean)
        return [s]
      }

      let showProgressModal =
        detailNeeded.length > 0 ||
        candidates.length > 1 ||
        candidates.some(listBoldMultiTx)

      setBoldBulkProgressOpen(showProgressModal)

      let detailDone = 0
      for (const order of candidates) {
        const main = normalizeOrderPaymentMethod(order.payment_method || order.extraDetails?.payment_method)
        const storeName = (order.store_name || '').trim()
        if (!storeName) continue

        if (orderNeedsPagosDetailForBoldVerify(order)) {
          detailDone += 1
          if (showProgressModal) {
            bumpProgress(
              detailDone,
              Math.max(detailNeeded.length, 1),
              `Obteniendo detalle del pedido #${order.id} (${detailDone}/${detailNeeded.length})…`
            )
          }
          const detailRes = await axiosInstance.get(`/list/${order.id}`, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
          })
          const detail = detailRes.data
          const pagos = (detail?.extraDetails?.pagos ?? detail?.pagos ?? []) as Array<{
            metodo_pago?: string
            id_transaccion?: string
          }>
          const totalStr = String(detail?.total ?? order.total ?? '0')
          for (const row of pagos) {
            if (normalizeOrderPaymentMethod(row.metodo_pago) !== 'bold') continue
            addJob(order.id, storeName, String(row.id_transaccion || ''), totalStr)
          }
        } else if (main === 'bold') {
          const raw = `${order.extraDetails?.transaction_id || order.transaction_id || ''}`
          for (const pid of txIdsFromField(raw)) {
            addJob(order.id, storeName, pid, String(order.total ?? '0'))
          }
        }
      }

      if (!showProgressModal && jobs.length > 1) {
        showProgressModal = true
        setBoldBulkProgressOpen(true)
      }

      const totalSteps = detailNeeded.length + jobs.length

      if (jobs.length === 0) {
        toast.error(
          'No hay tramos Bold con ID de transacción o link para verificar en los pedidos de la lista.',
          { icon: 'ℹ️', position: 'top-center' }
        )
        return
      }

      if (showProgressModal) {
        bumpProgress(
          detailNeeded.length,
          Math.max(1, totalSteps),
          'Verificando pagos Bold…'
        )
      }

      let processed = 0
      let updated = 0
      let errors = 0
      let cur = detailNeeded.length

      for (const job of jobs) {
        cur += 1
        if (showProgressModal) {
          bumpProgress(
            cur,
            Math.max(1, totalSteps),
            `Verificando Bold pedido #${job.orderId} (${cur - detailNeeded.length}/${jobs.length})…`
          )
        }
        try {
          const outcome = await verifyBoldPaymentOperation({
            token,
            orderId: job.orderId,
            storeName: job.storeName,
            paymentId: job.paymentId,
            orderTotal: job.orderTotal
          })
          if (outcome === 'updated') {
            processed += 1
            updated += 1
          } else if (outcome === 'processed_no_update') {
            processed += 1
            errors += 1
          } else {
            errors += 1
          }
        } catch (error) {
          console.error('Error verificando pedido BOLD:', error)
          errors += 1
        }
      }

      toast.success(
        `Verificación BOLD completada. Operaciones procesadas: ${processed}, actualizaciones confirmadas: ${updated}, errores o sin cambio: ${errors}`,
        { icon: '✅', duration: 7000, position: 'top-center' }
      )

      if (hasInitialized) {
        const storeFiltersForList = expandOrderListStoreFiltersForBold(
          selectedStores.map((store) => store.value)
        )
        await refreshOrderList({ stores: storeFiltersForList });
      } else {
        window.location.reload()
      }
    } finally {
      setIsVerifyingBold(false)
      setBoldBulkProgressOpen(false)
    }
  }, [
    orders,
    hasInitialized,
    refreshOrderList,
    selectedStores
  ]);

  const handleDelete = async (orderId: number) => {
    const confirmDelete = await new Promise((resolve) => {
      toast((t) => (
        <div className="flex flex-col">
          <p>¿Confirmas que deseas eliminar este pedido?</p>
          <div className="flex justify-end space-x-2 mt-2">
            <button onClick={() => { toast.dismiss(t.id); resolve(true); }} className="px-1 py-1 bg-red-500 text-white rounded">Eliminar</button>
            <button onClick={() => { toast.dismiss(t.id); resolve(false); }} className="px-1 py-1 bg-gray-300 rounded">Cancelar</button>
          </div>
        </div>
      ),
        { duration: Infinity }
      );
    });

    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("accessToken");
      const updateData = {
        status: "eliminado",
        meta_data: [
          {
            key: "fecha_eliminacion",
            value: new Date().toISOString()
          }
        ]
      };

      await axiosInstance.patch(`/list/${orderId}`, updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Pedido marcado como eliminado", { icon: "🗑️", position: 'top-center' });
      if (hasInitialized) {
        refreshOrderList();
      }
    } catch (error) {
      console.error("Error al cambiar el estado del pedido:", error);
      toast.error("Error al cambiar el estado", { icon: "⚠️", position: 'top-center' });
    }
  };


  // Ref para rastrear los valores anteriores y evitar consultas innecesarias
  const prevFilterValuesRef = useRef<string>('');
  // Ref para rastrear cambios en filtros (sin incluir currentPage) para detectar cuando resetear página
  const prevFiltersOnlyRef = useRef<string>('');
  // Ref para evitar doble disparo cuando se resetea currentPage por cambio de filtros
  const isResettingPageRef = useRef<boolean>(false);

  // useEffect consolidado para manejar todas las actualizaciones de datos
  // Solo se dispara cuando hay valores seleccionados (NO cuando solo se activa el switch)
  useEffect(() => {
    if (hasInitialized) {
      // Si estamos en medio de un reset de página, ignorar este disparo
      if (isResettingPageRef.current) {
        isResettingPageRef.current = false;
        return;
      }

      // Solo incluir filtros que estén activados Y tengan valores seleccionados
      const {
        states,
        stores,
        transportadoras,
        paymentMethods,
        destinos,
        dateRangeFilter,
        agente,
      } = getActiveOrderListFilters();

      // Crear una clave SOLO con filtros (sin currentPage) para detectar cambios en filtros
      const currentFiltersOnlyKey = JSON.stringify({
        states: states.sort().join(','),
        stores: stores.sort().join(','),
        transportadoras: transportadoras.sort().join(','),
        paymentMethods: paymentMethods.sort().join(','),
        destinos: destinos.sort().join(','),
        dateRange: dateRangeFilter ? `${dateRangeFilter.start}-${dateRangeFilter.end}` : '',
        agente: agente || '',
        search: debouncedSearchTerm,
        sortBy,
        order
      });

      // Crear una clave única con todos los valores incluyendo currentPage para la consulta
      const currentFilterKey = JSON.stringify({
        states: states.sort().join(','),
        stores: stores.sort().join(','),
        transportadoras: transportadoras.sort().join(','),
        paymentMethods: paymentMethods.sort().join(','),
        destinos: destinos.sort().join(','),
        dateRange: dateRangeFilter ? `${dateRangeFilter.start}-${dateRangeFilter.end}` : '',
        agente: agente || '',
        page: currentPage,
        pageSize,
        search: debouncedSearchTerm,
        sortBy,
        order
      });

      // Detectar si cambiaron los filtros (sin considerar currentPage)
      const filtersChanged = prevFiltersOnlyRef.current !== currentFiltersOnlyKey;
      
      // Si cambiaron los filtros (incluyendo search), resetear a página 1
      if (filtersChanged) {
        // Si ya había una clave previa y currentPage no es 1, resetear página
        if (prevFiltersOnlyRef.current !== '' && currentPage !== 1) {
          // Marcar que estamos reseteando la página para evitar doble disparo
          isResettingPageRef.current = true;
          setCurrentPage(1);
        } else {
          // Si currentPage ya es 1, asegurar que el flag esté limpio
          isResettingPageRef.current = false;
        }
        
        // Actualizar la referencia de filtros
        prevFiltersOnlyRef.current = currentFiltersOnlyKey;
        // Actualizar la referencia completa con page=1
        prevFilterValuesRef.current = JSON.stringify({
          states: states.sort().join(','),
          stores: stores.sort().join(','),
          transportadoras: transportadoras.sort().join(','),
          paymentMethods: paymentMethods.sort().join(','),
          destinos: destinos.sort().join(','),
          dateRange: dateRangeFilter ? `${dateRangeFilter.start}-${dateRangeFilter.end}` : '',
          agente: agente || '',
          page: 1,
          pageSize,
          search: debouncedSearchTerm,
          sortBy,
          order
        });
        
        // Hacer la consulta con página 1 (siempre, incluso si es la primera vez o si currentPage ya es 1)
        fetchOrders(
          1, 
          pageSize, 
          debouncedSearchTerm, 
          sortBy, 
          order, 
          states,
          stores,
          dateRangeFilter,
          transportadoras,
          paymentMethods,
          destinos,
          agente
        );
      } else {
        // Si no cambiaron los filtros, verificar si cambió algo más (página, pageSize, etc.)
        if (prevFilterValuesRef.current !== currentFilterKey) {
          prevFilterValuesRef.current = currentFilterKey;
          // Actualizar también la referencia de filtros si es la primera vez
          if (prevFiltersOnlyRef.current === '') {
            prevFiltersOnlyRef.current = currentFiltersOnlyKey;
          }
          
          fetchOrders(
            currentPage, 
            pageSize, 
            debouncedSearchTerm, 
            sortBy, 
            order, 
            states,
            stores,
            dateRangeFilter,
            transportadoras,
            paymentMethods,
            destinos,
            agente
          );
        } else {
          // Si no cambió nada, actualizar solo la referencia de filtros si es necesario
          if (prevFiltersOnlyRef.current === '') {
            prevFiltersOnlyRef.current = currentFiltersOnlyKey;
          }
        }
      }
    }
    // Comportamiento intencional: NO incluir filterByState, filterByStore, etc.
    // Solo incluir las selecciones para que se dispare cuando se seleccionan valores,
    // NO cuando solo se activa el switch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasInitialized, 
    currentPage, 
    pageSize, 
    debouncedSearchTerm, 
    sortBy, 
    order, 
    selectedStates, 
    selectedStores, 
    dateRange.start,
    dateRange.end,
    selectedTransportadoras, 
    selectedPaymentMethods, 
    selectedDestinos,
    selectedAgent,
    getActiveOrderListFilters,
    fetchOrders
  ]);

  useEffect(() => {
    // Agregar estilos CSS para el switch
    const style = document.createElement('style');
    style.textContent = `
      .toggle-checkbox:checked {right: 0;border-color: #3b82f6;}
      .toggle-checkbox:checked + .toggle-label {background-color: #3b82f6;}
      .react-select-container {width: 100%;}
      .react-select__control {border-color: #e5e7eb;boxShadow: none;}
      .react-select__control:hover {border-color: #3b82f6;}
      .react-select__multi-value {background-color: #e5e7eb;border-radius: 0.25rem;}
      .react-select__multi-value__label {color: #374151;padding: 0.25rem 0.5rem;}
      .react-select__multi-value__remove {color: #6b7280;}
      .react-select__multi-value__remove:hover {background-color: #d1d5db;color: #374151;}
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);


  // Función para formatear el total con separadores de miles y sin decimales
  const formatCurrency = (value: string | number) => {
    const number = Math.round(Number(value));
    return number.toLocaleString('es-CO');
  };

  // Función para obtener el icono/imagen según la transportadora
  const getTransportadoraIcon = (transportadora?: string, storeName?: string) => {
    const iconClasses = "h-5 w-5 mr-1 shrink-0 object-contain transition-transform duration-200 hover:scale-[1.75] cursor-pointer";
    const defaultIconClasses = "h-5 w-5 mr-1 shrink-0 text-gray-400 transition-transform duration-200 hover:scale-[1.75] cursor-pointer";
    // Si es Mercado Libre y no es fulfillment, usar color amarillo
    const isMercadoLibre = storeName === 'Mercado Libre';
    const defaultIconColor = isMercadoLibre && transportadora !== 'fulfillment' 
      ? "h-5 w-5 mr-1 shrink-0 text-yellow-500 transition-transform duration-200 hover:scale-[1.75] cursor-pointer"
      : defaultIconClasses;

    if (!transportadora) {
      return <TruckIcon className={defaultIconColor} />;
    }

    switch (transportadora) {
      case '99minutos':
        return <img src={logo99minutos} alt="99minutos" className={iconClasses} />;
      case 'Envía':
        return <img src={logoEnvia} alt="Envía" className={iconClasses} />;
      case 'Inter Rapidisimo':
        return <img src={logoInter} alt="Inter Rapidisimo" className={iconClasses} />;
      case 'TCC':
        return <img src={logoTCC} alt="TCC" className={iconClasses} />;
      case 'Coordinadora':
        return <img src={logoCoordinadora} alt="Coordinadora" className={iconClasses} />;
      case 'deprisa':
        return <img src={logoDeprisa} alt="Deprisa" className={iconClasses} />;
      case 'Servientrega':
        return <img src={logoServientrega} alt="Servientrega" className={iconClasses} />;
      case 'fulfillment':
        return <BoltIcon className="h-5 w-5 min-w-[20px] shrink-0 text-yellow-500 mr-1 transition-transform duration-200 hover:scale-[1.75] cursor-pointer" />;
      case 'Entrega PDV':
        return <img src="/favicon.ico" alt="Entrega PDV" className={iconClasses} />;
      case 'Mensajero interno':
      case 'xd_drop_off':
        // Para estas transportadoras sin logo, mostrar icono genérico
        return <TruckIcon className={defaultIconColor} />;
      default:
        return <TruckIcon className={defaultIconColor} />;
    }
  };

  const columns = [
    {
      header: 'ID',
      accessor: 'id' as keyof Order,
      width: '90px',
    },
    {
      key: 'col_origen',
      header: (
        <div className="flex items-center space-x-2">
          <span>ORIGEN</span>
          <button
            onClick={() => setShowOriginOrderId(!showOriginOrderId)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title={showOriginOrderId ? "Ocultar Origin Order ID" : "Mostrar Origin Order ID"}
          >
            <PlusIcon className={`h-4 w-4 text-custom-blue transition-transform ${showOriginOrderId ? 'rotate-45 text-red-500' : ''}`} />
          </button>
        </div>
      ),
      width: showOriginOrderId ? '150px' : '160px',
      cellOverflowVisible: true,
      accessor: 'orders_id' as keyof Order,
      cell: (row: Order) => (
        <span className="text-sm text-gray-600 flex items-center min-w-0">
          {getTransportadoraIcon(row.transportadora, row.store_name)}
          <span className="whitespace-nowrap">
            {row.orders_id || row.origin_order_id}
          </span>
        </span>
      ),
    },
    // Columna condicional para Origin Order ID
    ...(showOriginOrderId ? [{
      header: 'ORIGEN EXTERNO',
      width: '150px',
      accessor: 'origin_order_id' as keyof Order,
      cell: (row: Order) => (
       <span className="text-sm text-gray-600 whitespace-nowrap">
          {row.origin_order_id || ''}
       </span>
      ),
    }] : []),
    {
      header: 'Nombre',
      accessor: 'customer_full_name' as keyof Order,
      width: '158px',
      cell: (row: Order) => {
        const nombre = row.customer_full_name || '';
        const nombreMax = 23;
        const nombreTruncado = nombre.length > nombreMax ? nombre.substring(0, nombreMax) + '..' : nombre;
        return (
          <span className="text-sm" title={nombre}>
            {nombreTruncado}
          </span>
        );
      },
    },
    {
      header: 'Tienda',
      width: '55px',
      accessor: 'store_name' as keyof Order,
      cell: (row: Order) => {
        const store = STORE_STYLES.find(s => s.value === row.store_name);
        if (!store) {
          return <span className="text-gray-500">{row.store_name ?? ''}</span>;
        }
        
        return <span className={store.color}>{store.shortValue}</span>;
      },
    },
     {
       header: (
         <div className="flex items-center gap-1.5">
           <span>Fecha</span>
           <button
             type="button"
             onClick={(e) => {
               e.stopPropagation()
               setShowFullDateTime(!showFullDateTime)
             }}
             className="p-0.5 rounded hover:bg-gray-100 transition-colors shrink-0"
             title={showFullDateTime ? 'Ocultar hora' : 'Mostrar hora'}
             aria-label={showFullDateTime ? 'Ocultar hora' : 'Mostrar hora'}
           >
             <PlusIcon className={`h-4 w-4 text-gray-500 transition-transform ${showFullDateTime ? 'rotate-45' : ''}`} />
           </button>
         </div>
       ),
       accessor: 'date_created' as keyof Order,
       /** Evita puntos suspensivos del DataTable; ancho acorde a fecha+hora en español */
       width: showFullDateTime ? '175px' : '108px',
       cellOverflowVisible: true,
       cell: (row: Order) => {
         if (!row.date_created) {
           return <span className="text-xs text-gray-400">-</span>;
         }
         
         if (showFullDateTime) {
           return (
             <span className="block text-xs leading-snug whitespace-normal break-words" title={row.date_created}>
               {row.date_created}
             </span>
           );
         }
         const dateOnly = row.date_created.split(',')[0]?.trim() || row.date_created
         return (
           <span className="block text-xs leading-snug whitespace-normal" title={row.date_created}>
             {dateOnly}
           </span>
         );
       },
     },
    {
      header: (
        <div className="flex items-center gap-2">
          <span>#OFIMA</span>
          <button
            onClick={(e) => {e.stopPropagation();handleForceSync();}}
            disabled={isSyncing}
            className="p-0 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Consultar remisiones en OFIMA (excluye: eliminado, verificar transacción, cancelado, pendiente, fallido, pago fallido, devolución de dinero)"
          >
            <ArrowPathIcon 
              className={`h-4 w-4 text-blue-600 ${isSyncing ? 'animate-spin' : ''}`} 
            />
          </button>
        </div>
      ),
      accessor: 'id_remision' as keyof Order,
      width: '100px',
      /** Popover FS/NC usa position absolute; el DataTable no debe recortar la celda */
      cellOverflowVisible: true,
      cell: (row: Order) => {
        const nMensaje =  mensajesSinRemision.get(row.id)==='Pendiente Saldo'?'Sin Saldo':mensajesSinRemision.get(row.id);
        const mensaje = nMensaje;
        // Convertir id_remision a string y verificar que no esté vacío
        const idRemisionStr = row.id_remision ? String(row.id_remision).trim() : '';
        const tieneRemision = idRemisionStr !== '';
        
        // Si no tiene remisión pero tiene mensaje, mostrar el mensaje
        if (!tieneRemision && mensaje) {
          return (
            <span 
              className="text-sm text-orange-600 font-medium" 
              title={`Sin remisión: ${mensaje}`}
            >
              {mensaje}
            </span>
          );
        }
        
        // Si tiene remisión: número OFIMA + «+» con FS/NC si el API los envía
        if (tieneRemision) {
          return (
            <OfimaRemisionWithConsecutivos
              variant="compact"
              idRemision={idRemisionStr}
              ofimaConsecutivos={row.ofima_consecutivos ?? null}
            />
          );
        }
        
        // Si no tiene remisión ni mensaje, mostrar guión
        return (
          <span className="text-sm text-gray-400">
            -
          </span>
        );
      },
    },
    {
      header: 'Estado',
      width: '110px',
      accessor: 'status' as keyof Order,
      cell: (row: Order) => {
        const state = ORDER_STATES.find(s => s.value === row.status);
        
        if (!state) {
          return (
            <span className="text-center text-xs font-medium max-w-[90px] w-full truncate inline-block p-1 rounded-md text-gray-500 border border-sky-500">
              {row.status}
            </span>
          );
        }

        return (
          <span className={`text-center text-xs font-medium max-w-[90px] w-full truncate inline-block px-1 py-2 rounded-md ${state.color}`}>
            {state.label}
          </span>
        );
      },
    },
    {
      header: 'Total',
      accessor: 'total' as keyof Order,
      width: '110px',
      cell: (row: Order) => (
        <span>$ {formatCurrency(row.total)}</span>
      ),
    },
    // Columnas de documentos solo visibles con permiso view_documents
    ...(hasPermission('view_documents') ? [
      {
        key: 'col_pedido_doc1',
        header: 'Doc 1',
        accessor: 'documento1' as keyof Order,
        width: '64px',
        maxWidth: '72px',
        cell: (row: Order) => {
          const docState = openedDocuments.get(row.id);
          const isOpened = docState?.doc1 || false;
          
          return (
            <Document1Cell 
              row={row} 
              isOpened={isOpened} 
              onOpenDocument={handleOpenDocument}
              onFalabellaShippingLabel={handleFalabellaShippingLabel}
              onMercadoLibreShippingLabel={handleMercadoLibreShippingLabel}
            />
          );
        },
      },
      {
        key: 'col_pedido_doc2',
        header: 'Doc 2',
        accessor: 'documento2' as keyof Order,
        width: '64px',
        maxWidth: '72px',
        cell: (row: Order) => {
          const docState = openedDocuments.get(row.id);
          const isOpened = docState?.doc2 || false;
          
          return (
            <Document2Cell 
              row={row} 
              isOpened={isOpened} 
              onOpenDocument={handleOpenDocument}
            />
          );
        },
      }
    ] : []),
    {
      header: 'Acciones',
      accessor: 'id' as keyof Order,
      cell: (row: Order) => (
        <div className="flex space-x-2">
          <button 
            onClick={(e) => {
              const currentUrl = window.location.pathname + window.location.search;
              navigate(`/list/${row.id}`, {
                state: { 
                  from: currentUrl 
                }
              });
            }}
            onMouseDown={(e) => {
              // Detectar clic de la rueda del mouse (button 1)
              if (e.button === 1) {
                e.preventDefault();
              //  const currentUrl = window.location.pathname + window.location.search;
                const url = `/list/${row.id}`;
                window.open(url, '_blank');
              }
            }}
            className="text-green-500 hover:text-green-700" 
            title="Ver (Clic rueda: nueva pestaña)"
          >
            <EyeIcon className="h-6 w-6" />
          </button>
          {hasPermission('manage_orders') && (
            <button 
              onClick={(e) => {
                const currentUrl = window.location.pathname + window.location.search;
                navigate(`/edit/${row.id}`, {
                  state: { 
                    from: currentUrl 
                  }
                });
              }}
              onMouseDown={(e) => {
                // Detectar clic de la rueda del mouse (button 1)
                if (e.button === 1) {
                  e.preventDefault();
                 // const currentUrl = window.location.pathname + window.location.search;
                  const url = `/edit/${row.id}`;
                  window.open(url, '_blank');
                }
              }}
              className="text-blue-500 hover:text-blue-700" 
              title="Editar (Clic rueda: nueva pestaña)"
            >
              <PencilSquareIcon className="h-6 w-6" />
            </button>
          )}
          {/* Botón Generar Comprobante */}
          {(hasPermission('generar_comprobante') || hasPermission('manage_orders')) && (
          <button
            onClick={async () => {
              try {
                toast.loading('Generando comprobante...', { position: 'top-center' }); 
                await generarPDFComprobante(row.id);
                toast.dismiss();
                toast.success('Comprobante generado exitosamente', { position: 'top-center' });
              } catch (error) {
                console.error('Error generando comprobante:', error);
                toast.error('Error al generar el comprobante', { position: 'top-center' });
                toast.dismiss();
              }
            }}
            className="text-orange-500 hover:text-orange-700"
            title="Generar Comprobante">
            <DocumentArrowDownIcon className="h-6 w-6" />
          </button>
          )}
          
          {/* Botón Actualizar Documentos */}
          {hasPermission('add_documents') && (
            <button
              onClick={() => handleUpdateDocuments(row)}
              disabled={row.status === 'completed'}
              className={row.status === 'completed' ? 'text-gray-300 cursor-not-allowed' : 'text-indigo-500 hover:text-indigo-700'}
              title={row.status === 'completed' ? 'No disponible para pedidos completados' : 'Actualizar Documentos'}>
              <DocumentTextIcon className="h-6 w-6" />
            </button>
          )}
           {((row.status === 'incidencia' && hasPermission('incidencia_resuelta')) ||
            (row.status === 'incidencia-resuelta' && hasPermission('incidencia_resuelta')) ||
            (row.status !== 'incidencia' && row.status !== 'incidencia-resuelta' && hasPermission('manage_incidencia'))) && (
            <button
              onClick={() => handleIncidenciaOrder(row)}
              className={
                row.status === 'incidencia' 
                  ? "text-black hover:text-gray-800" 
                  : row.status === 'incidencia-resuelta'
                  ? "text-green-500 hover:text-green-700"
                  : "text-gray-500 hover:text-gray-700"
              }
              title={
                row.status === 'incidencia' 
                  ? "Resolver Incidencia" 
                  : row.status === 'incidencia-resuelta'
                  ? "Marcar como Incidencia"
                  : "Marcar como Incidencia"
              }>
              {row.status === 'incidencia-resuelta' ? (
                <CheckCircleIcon className="h-6 w-6" />
              ) : (
                <ExclamationTriangleIcon className="h-6 w-6" />
              )}
            </button>
          )}

          {/* Botón Cancelar Orden — permiso general (jerarquía superior, condición sin cambios) */}
          {hasPermission('cancelar_pedido') && row.status !== 'cancelled' && (
            <button
              onClick={() => handleCancelOrder(row)}
              className="text-orange-500 hover:text-orange-700"
              title="Cancelar Orden">
              <XCircleIcon className="h-6 w-6" />
            </button>
          )}
          {/* Fulfillment (MKP): inferior; solo si el permiso general no habilita cancelar */}
          {hasPermission('cancelar_pedido_mkp')
            && row.transportadora === 'fulfillment'
            && row.status !== 'cancelled'
            && !(hasPermission('cancelar_pedido') && row.status !== 'cancelled')
            && (
            <button
              onClick={() => handleCancelOrder(row)}
              className="text-orange-500 hover:text-orange-700"
              title="Cancelar Orden">
              <XCircleIcon className="h-6 w-6" />
            </button>
          )}
          
          {hasPermission('delete_orders') && (
            <button onClick={() => handleDelete(row.id)} className="text-red-500 hover:text-red-700" title="Eliminar">
              <TrashIcon className="h-6 w-6" />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Manejadores de filtros
  const handleFilterByState = (checked: boolean) => {
    setFilterByState(checked);
    if (!checked) {
      setSelectedStates([]);
    }
  };

  const handleFilterByStore = (checked: boolean) => {
    setFilterByStore(checked);
    if (!checked) {
      setSelectedStores([]);
    }
  };

  const handleFilterByTransportadora = (checked: boolean) => {
    setFilterByTransportadora(checked);
    if (!checked) {
      setSelectedTransportadoras([]);
    }
  };

  const handleFilterByPaymentMethod = (checked: boolean) => {
    setFilterByPaymentMethod(checked);
    if (!checked) {
      setSelectedPaymentMethods([]);
    }
  };

  const handleFilterByDestino = (checked: boolean) => {
    setFilterByDestino(checked);
    if (!checked) {
      setSelectedDestinos([]);
    }
  };

  const handleFilterByDate = (checked: boolean) => {
    setFilterByDate(checked);
    if (!checked) {
      setDateRange({ start: '', end: '' });
    }
  };

  const handleFilterByAgent = (checked: boolean) => {
    setFilterByAgent(checked);
    if (!checked) {
      setSelectedAgent('');
    }
  };

  const selectedSalesAgentOption = useMemo(() => {
    if (!selectedAgent) return null;
    const agent = salesAgents.find((a) => a.id.toString() === selectedAgent);
    if (!agent) {
      return { label: 'Seleccionar agente...', value: selectedAgent };
    }
    return {
      label: `${agent.first_name} ${agent.last_name}`.trim(),
      value: selectedAgent,
    };
  }, [selectedAgent, salesAgents]);

  // Calcular cantidad de filtros activos (solo cuenta cuando hay valores seleccionados)
  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (searchTerm) count++;
    // Solo contar si el switch está activado Y hay valores seleccionados
    if (filterByState && selectedStates.length > 0) count++;
    if (filterByStore && selectedStores.length > 0) count++;
    if (filterByTransportadora && selectedTransportadoras.length > 0) count++;
    if (filterByPaymentMethod && selectedPaymentMethods.length > 0) count++;
    if (filterByDestino && selectedDestinos.length > 0) count++;
    if (filterByDate && dateRange.start && dateRange.end) count++;
    if (filterByAgent && selectedAgent) count++;
    return count;
  };

  // Limpiar todos los filtros
  const handleClearAllFilters = () => {
    setSearchTerm('');
    setFilterByState(false);
    setFilterByStore(false);
    setFilterByTransportadora(false);
    setFilterByPaymentMethod(false);
    setFilterByDestino(false);
    setFilterByDate(false);
    setFilterByAgent(false);
    setSelectedStates([]);
    setSelectedStores([]);
    setSelectedTransportadoras([]);
    setSelectedPaymentMethods([]);
    setSelectedDestinos([]);
    setSelectedAgent('');
    setDateRange({ start: '', end: '' });
  };

  // Preparar secciones de filtros para FilterPanel
  const storeFilterOptions = useMemo(() => {
    const base = STORE_STYLES.filter((store) => store.value !== 'OFIMA');
    if (!allowedStoreNames) {
      return base.map((store) => ({ label: store.label, value: store.value }));
    }
    const allowed = new Set<string>(allowedStoreNames);
    return base
      .filter((store) => allowed.has(store.value))
      .map((store) => ({ label: store.label, value: store.value }));
  }, [allowedStoreNames]);

  const filterSections: FilterSection[] = [
    {
      id: 'quick',
      title: 'Filtros Rápidos',
      filters: [
        {
          id: 'state',
          label: 'Filtrar por estado',
          type: 'switch',
          checked: filterByState,
          onToggle: handleFilterByState,
          selectProps: filterByState ? {
            isMulti: true,
            options: ORDER_STATES.map(state => ({ label: state.label, value: state.value })),
            value: selectedStates,
            onChange: (newValue) => setSelectedStates(newValue as { label: string; value: string }[]),
            placeholder: 'Seleccionar estados...'
          } : undefined
        },
        {
          id: 'store',
          label: 'Filtrar por tienda',
          type: 'switch',
          checked: filterByStore,
          onToggle: handleFilterByStore,
          selectProps: filterByStore ? {
            isMulti: true,
            options: storeFilterOptions,
            value: selectedStores,
            onChange: (newValue) => {
              const picked = (newValue as { label: string; value: string }[]) || [];
              const values = sanitizeStoreFilterValues(
                user?.role_id,
                picked.map((s) => s.value)
              );
              setSelectedStores(
                values.map((v) => {
                  const opt = storeFilterOptions.find((o) => o.value === v);
                  return { label: opt?.label ?? v, value: v };
                })
              );
            },
            placeholder: 'Seleccionar tiendas...'
          } : undefined
        },
        {
          id: 'date',
          label: 'Filtrar por fecha',
          type: 'switch',
          checked: filterByDate,
          onToggle: handleFilterByDate,
          dateRangeProps: filterByDate ? {
            start: dateRange.start,
            end: dateRange.end,
            onStartChange: (value) => setDateRange(prev => ({ ...prev, start: value })),
            onEndChange: (value) => setDateRange(prev => ({ ...prev, end: value }))
          } : undefined
        }
      ]
    },
    {
      id: 'advanced',
      title: 'Filtros Avanzados',
      filters: [
        {
          id: 'transportadora',
          label: 'Filtrar por transportadora',
          type: 'switch',
          checked: filterByTransportadora,
          onToggle: handleFilterByTransportadora,
          selectProps: filterByTransportadora ? {
            isMulti: true,
            options: transportadora.map(t => ({ label: t.label, value: t.value })),
            value: selectedTransportadoras,
            onChange: (newValue) => setSelectedTransportadoras(newValue as { label: string; value: string }[]),
            placeholder: 'Seleccionar transportadoras...'
          } : undefined
        },
        {
          id: 'payment',
          label: 'Filtrar por método de pago',
          type: 'switch',
          checked: filterByPaymentMethod,
          onToggle: handleFilterByPaymentMethod,
          selectProps: filterByPaymentMethod ? {
            isMulti: true,
            options: metodoPago.map(pm => ({ label: pm.label, value: pm.value })),
            value: selectedPaymentMethods,
            onChange: (newValue) => setSelectedPaymentMethods(newValue as { label: string; value: string }[]),
            placeholder: 'Seleccionar métodos de pago...'
          } : undefined
        },
        {
          id: 'destino',
          label: 'Filtrar por destino del pedido',
          type: 'switch',
          checked: filterByDestino,
          onToggle: handleFilterByDestino,
          selectProps: filterByDestino ? {
            isMulti: false,
            options: [
              { label: 'Área Metropolitana', value: 'metropolitana' },
              { label: 'Nacional', value: 'nacional' }
            ],
            value: selectedDestinos[0] || null,
            onChange: (newValue) => setSelectedDestinos(newValue ? [newValue] : []),
            placeholder: 'Seleccionar destino...'
          } : undefined
        },
        {
          id: 'agent',
          label: 'Agente de ventas',
          type: 'switch',
          checked: filterByAgent,
          onToggle: handleFilterByAgent,
          selectProps: filterByAgent ? {
            isMulti: false,
            options: salesAgents.map((agent) => ({
              label: `${agent.first_name} ${agent.last_name}`.trim(),
              value: agent.id.toString(),
            })),
            value: selectedSalesAgentOption,
            onChange: (newValue) => setSelectedAgent(newValue?.value || ''),
            placeholder: 'Seleccionar agente de ventas...'
          } : undefined
        }
      ]
    }
  ];
 //const totalCountAsignado = Math.round(parseFloat(statistics.asignado?.totalValue || 0)).toLocaleString(); 

  return (
    <>
    <div className="w-full h-full p-0 overflow-visible" style={{ maxWidth: '100%', width: '100%', boxSizing: 'border-box' }}>
      <div className="w-full mx-auto" style={{ maxWidth: '100%', width: '100%', boxSizing: 'border-box' }}>
        <div className="flex flex-col gap-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-8 gap-4 mb-4">
       
        <MetricCard title="Verificar Transaccion" 
        value={statistics.verify_transaction?.count || 0} 
        icon={<CurrencyDollarIcon className="h-7 w-7 text-blue-400" />}
        color="blue"
        />
        
        <MetricCard title="Procesando" 
        value={statistics.processing?.count || 0} 
        icon={<ArrowPathIcon className="h-7 w-7 text-purple-500" />} 
        color="purple"
       />

        <MetricCard title="Asignados" 
        value={statistics.asignado?.count || 0} 
        icon={<UserIcon className="h-7 w-7 text-indigo-500" />} 
        color="indigo"
       />

        <MetricCard title="Picking Iniciado" 
        value={statistics.picking_started?.count || 0} 
        icon={<ClockIcon className="h-7 w-7 text-pink-500" />} 
        color="pink"
       />
        <MetricCard title="Empacado" 
        value={statistics.empacado?.count || 0} 
        //cuantity={statistics.empacado?.totalValue || 0}
        icon={<CubeIcon className="h-7 w-7 text-orange-500" />} 
        color="orange"
       // icon={<CubeIcon className="h-7 w-7 text-orange-500 bg-orange-100 rounded-full p-1 " />} 
       />

        <MetricCard title="Enviado" 
        value={statistics.enviado?.count || 0} 
        //cuantity={statistics.enviado?.totalValue || 0}
        icon={<TruckIcon className="h-7 w-7 text-teal-500" />} 
        color="teal"
       />
       
        <MetricCard title="Completado" 
        value={statistics.completed?.count || 0} 
        //cuantity={statistics.completed?.totalValue || 0}
        icon={<CheckCircleIcon className="h-7 w-7 text-green-500" />} 
        color="green"
       />
        <MetricCard title="Total Pedidos" 
        value={totalPedidos} 
        //cuantity={statistics.completed?.totalValue || 0}
        icon={<ShoppingBagIcon className="h-7 w-7 text-custom-blue" />} 
        color="blue"
       />


        </div>
          <div className="flex items-center justify-between">
            <h3>Lista de Pedidos</h3>
            <div className="flex items-center gap-4">
              <Button
                onClick={handleVerifyBoldOrders}
                variant="secondary"
                disabled={isVerifyingBold || isLoading}
                className="flex items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 shadow-sm h-9 w-9 !p-0"
                aria-label={isVerifyingBold ? 'Verificando pagos BOLD' : 'Verificar pagos BOLD'}
              >
                <img
                  src={boldIcon}
                  alt="BOLD"
                  className={`h-7 w-7 object-contain ${isVerifyingBold ? 'animate-spin' : ''}`}
                />
              </Button>
              {/* Botón de filtros con contador */}
              <div className="relative overflow-visible">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFilters(!showFilters);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  variant="secondary"
                  leftIcon={FunnelIcon}
                  className="relative"
                >
                  Filtros
                  {getActiveFiltersCount() > 0 && (
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs font-bold bg-blue-600 text-white rounded-full min-w-[20px] flex items-center justify-center">
                      {getActiveFiltersCount()}
                    </span>
                      )}
                </Button>
                <FilterPanel
                  isOpen={showFilters}
                  onClose={() => setShowFilters(false)}
                  sections={filterSections}
                  onClearAll={handleClearAllFilters}
                  activeFiltersCount={getActiveFiltersCount()}
                  position="right"
                />
              </div>
              {hasPermission('manage_orders') && (
                <Button
                  onClick={() => navigate('/orders/new')}
                  variant="success"
                  leftIcon={PlusIcon}
                  fullWidth
                  className="sm:w-auto"
                >
                  Crear
                </Button>
              )}
            </div>
          </div>

          <div 
            className="w-full mb-8 md:mb-20 relative min-h-[200px] overflow-x-auto" 
            style={{ 
              maxWidth: 'calc(100% - 2px)', 
              width: 'calc(100% - 2px)',
              contain: 'layout',
              boxSizing: 'border-box'
            }}
          >
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-20">
                <LoadingSpinner size="large" />
              </div>
            )}
            <div 
              className={`inline-grid ${isLoading ? 'opacity-30 pointer-events-none' : ''}`}
              style={{ minWidth: '100%' }}
            > 
              <DataTable data={orders} columns={columns} className="w-full"/>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>

      {b2bFacturaModal?.open ? (
        <B2bOfimaFacturaProgressModal
          isOpen
          lines={b2bFacturaModal.lines}
          currentIndex={b2bFacturaModal.currentIndex}
          isRunning={b2bFacturaModal.running}
        />
      ) : null}

      {/* Modal para actualizar documentos */}
      <UpdateDocumentsModal
        {...({
          isOpen: updateDocumentsModal.isOpen,
          onClose: () => setUpdateDocumentsModal(prev => ({ ...prev, isOpen: false })),
          orderId: updateDocumentsModal.orderId,
          currentDocumento1: updateDocumentsModal.documento1,
          currentDocumento2: updateDocumentsModal.documento2,
          currentTransportadora: updateDocumentsModal.transportadora,
          currentGuia: updateDocumentsModal.guia,
          storeName: updateDocumentsModal.storeName,
          onSuccess: handleDocumentsUpdateSuccess
        } as any)}
      />

      <ExportProgressModal
        isOpen={boldBulkProgressOpen}
        progress={boldBulkProgress}
        message={boldBulkMessage}
        title="Verificación Bold"
        unitLabel="pasos"
      />

      {/* Modal para cancelar orden */}
      <CancelOrderModal
        isOpen={cancelOrderModal.isOpen}
        onClose={() => {
          setCancelOrderModal({ isOpen: false, order: null });
          setCancelSteps(INITIAL_CANCEL_STEPS);
        }}
        order={cancelOrderModal.order}
        onConfirm={handleConfirmCancelOrder}
        isLoading={isCancelling}
        cancelSteps={cancelSteps}
      />

      {/* Modal de etiqueta de envío Falabella */}
      <FalabellaShippingLabelModal
        isOpen={falabellaShippingModal.isOpen}
        onClose={() => setFalabellaShippingModal({ isOpen: false, order: null })}
        ordersId={falabellaShippingModal.order?.orders_id || ''}
        orderNumber={falabellaShippingModal.order?.orders_id}
      />

      {/* Modal de etiqueta de envío MercadoLibre */}
      <MercadoLibreShippingLabelModal
        isOpen={mercadolibreShippingModal.isOpen}
        onClose={() => setMercadoLibreShippingModal({ isOpen: false, order: null })}
        ordersId={mercadolibreShippingModal.order?.origin_order_id || ''}
        orderNumber={mercadolibreShippingModal.order?.orders_id}
      />

      {/* Modal de Gestión de Incidencias */}
      {incidenciaModal.order && incidenciaModal.targetStatus && (
        <IncidenciaModal
          isOpen={incidenciaModal.isOpen}
          onClose={() => setIncidenciaModal({ isOpen: false, order: null, targetStatus: null })}
          order={incidenciaModal.order}
          currentStatus={incidenciaModal.order.status as 'incidencia' | 'incidencia-resuelta'}
          onConfirm={handleConfirmIncidencia}
          isLoading={isUpdatingIncidencia}
        />
      )}
    </>
  );
};

export default OrdersTable;